import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Leaf, Wind, Utensils, Moon, Zap, Shield, Droplets, Heart, Brain,
  Thermometer, AlertTriangle, ArrowRight, CheckCircle
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const iconMap = {
  Wind, Utensils, Moon, Zap, Shield, Droplets, Heart, Brain, Activity: Zap
};

export default function Home() {
  const navigate = useNavigate();
  const [needs, setNeeds] = useState([]);
  const [selectedNeed, setSelectedNeed] = useState(null);
  const [preferCold, setPreferCold] = useState(false);
  const [loading, setLoading] = useState(true);
  const [userKit, setUserKit] = useState([]);

  useEffect(() => {
    fetchNeeds();
    // Load user kit from localStorage
    const savedKit = localStorage.getItem('acupedia_kit');
    if (savedKit) {
      setUserKit(JSON.parse(savedKit));
    }
  }, []);

  const fetchNeeds = async () => {
    try {
      const response = await axios.get(`${API}/needs`);
      setNeeds(response.data);
    } catch (error) {
      console.error('Error fetching needs:', error);
    } finally {
      setLoading(false);
    }
  };

  const getIcon = (iconName) => {
    const IconComponent = iconMap[iconName] || Leaf;
    return IconComponent;
  };

  const handleNeedSelect = (need) => {
    setSelectedNeed(need);
    // Save selection to localStorage for results page
    localStorage.setItem('acupedia_selected_need', JSON.stringify(need));
    localStorage.setItem('acupedia_prefer_cold', JSON.stringify(preferCold));
  };

  const handleContinue = () => {
    if (userKit.length === 0) {
      navigate('/kit');
    } else {
      navigate('/intake');
    }
  };

  return (
    <div className="animate-fade-in">
      {/* Hero Section */}
      <section className="hero-pattern py-16 md:py-24">
        <div className="container-acupedia">
          <div className="max-w-3xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 bg-acupedia-teal/10 text-acupedia-teal px-4 py-2 rounded-full mb-6">
              <Leaf className="w-4 h-4" />
              <span className="text-sm font-medium">Medicina tradițională chineză</span>
            </div>
            
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl text-acupedia-teal mb-6 leading-tight">
              Ceaiuri terapeutice<br />
              <span className="text-acupedia-gold">personalizate pentru tine</span>
            </h1>
            
            <p className="text-stone-600 text-lg md:text-xl mb-8 max-w-2xl mx-auto">
              Descoperă rețete de ceai adaptate nevoilor tale, bazate pe medicina tradițională 
              și pregătite din trusa ta de plante medicinale.
            </p>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Button
                onClick={() => navigate('/kit')}
                className="btn-primary w-full sm:w-auto"
                data-testid="cta-kit-btn"
              >
                <Leaf className="w-5 h-5 mr-2" />
                Alege ce ai în kit
              </Button>
              <Button
                variant="outline"
                onClick={() => document.getElementById('needs-section').scrollIntoView({ behavior: 'smooth' })}
                className="btn-secondary w-full sm:w-auto"
                data-testid="cta-needs-btn"
              >
                Spune-mi ce ai nevoie azi
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Medical Disclaimer */}
      <section className="py-6 bg-amber-50/50">
        <div className="container-acupedia">
          <div className="medical-disclaimer flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-acupedia-gold flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-medium text-stone-800 mb-1">Disclaimer Medical</p>
              <p className="text-sm text-stone-600">
                Acest conținut are scop educativ și nu înlocuiește consultul medical. 
                <strong> Dacă simptomele persistă peste 72h, aveți febră, dificultăți de respirație, 
                sânge sau scădere în greutate, consultați imediat un medic.</strong>
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Temperature Preference */}
      <section className="py-12 bg-white border-y border-stone-200">
        <div className="container-acupedia">
          <div className="max-w-2xl mx-auto">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 p-6 bg-stone-50 rounded-xl border border-stone-200">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-full bg-acupedia-teal/10 flex items-center justify-center flex-shrink-0">
                  <Thermometer className="w-6 h-6 text-acupedia-teal" />
                </div>
                <div>
                  <h3 className="font-heading text-lg text-stone-800 mb-1">
                    Preferi ceai CALD sau RECE?
                  </h3>
                  <p className="text-sm text-stone-600">
                    {preferCold 
                      ? 'Infuzia la rece extrage mai blând compușii și este ideală vara.'
                      : 'Ceaiul cald activează mai rapid compușii și încălzește corpul.'}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Label htmlFor="temp-pref" className={`text-sm font-medium ${!preferCold ? 'text-orange-600' : 'text-stone-400'}`}>
                  Cald
                </Label>
                <Switch
                  id="temp-pref"
                  checked={preferCold}
                  onCheckedChange={(checked) => {
                    setPreferCold(checked);
                    localStorage.setItem('acupedia_prefer_cold', JSON.stringify(checked));
                  }}
                  data-testid="temp-preference-switch"
                />
                <Label htmlFor="temp-pref" className={`text-sm font-medium ${preferCold ? 'text-cyan-600' : 'text-stone-400'}`}>
                  Rece
                </Label>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Needs Selection */}
      <section id="needs-section" className="py-16 md:py-24">
        <div className="container-acupedia">
          <div className="text-center mb-12">
            <h2 className="section-header">Ce ai nevoie astăzi?</h2>
            <p className="section-subheader mx-auto">
              Alege simptomul sau nevoia ta pentru a primi o rețetă personalizată
            </p>
          </div>

          {loading ? (
            <div className="needs-grid">
              {[...Array(8)].map((_, i) => (
                <div key={i} className="skeleton h-32 rounded-xl"></div>
              ))}
            </div>
          ) : (
            <div className="needs-grid animate-stagger">
              {needs.map((need) => {
                const Icon = getIcon(need.icon);
                const isSelected = selectedNeed?.id === need.id;
                
                return (
                  <button
                    key={need.id}
                    onClick={() => handleNeedSelect(need)}
                    className={`need-card ${isSelected ? 'selected' : ''}`}
                    data-testid={`need-${need.id}`}
                  >
                    <div className={`w-12 h-12 rounded-full flex items-center justify-center mb-3 transition-colors duration-200 ${
                      isSelected ? 'bg-acupedia-teal text-white' : 'bg-acupedia-teal/10 text-acupedia-teal'
                    }`}>
                      <Icon className="w-6 h-6" />
                    </div>
                    <h3 className="font-heading text-lg text-stone-800 mb-1">{need.name_ro}</h3>
                    <p className="text-xs text-stone-500 text-center line-clamp-2">{need.description}</p>
                    {isSelected && (
                      <CheckCircle className="absolute top-3 right-3 w-5 h-5 text-acupedia-gold" />
                    )}
                  </button>
                );
              })}
            </div>
          )}

          {/* Continue Button */}
          {selectedNeed && (
            <div className="mt-12 text-center animate-fade-in">
              <div className="inline-flex flex-col items-center gap-4 p-6 bg-acupedia-teal/5 rounded-xl border border-acupedia-teal/20">
                <p className="text-stone-700">
                  Ai selectat: <strong className="text-acupedia-teal">{selectedNeed.name_ro}</strong>
                </p>
                <Button
                  onClick={handleContinue}
                  className="btn-primary"
                  data-testid="continue-btn"
                >
                  {userKit.length === 0 ? 'Configurează Trusa Ta' : 'Continuă către Profil'}
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
                {userKit.length > 0 && (
                  <p className="text-xs text-stone-500">
                    {userKit.length} plante în trusa ta
                  </p>
                )}
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 bg-acupedia-teal-dark text-white">
        <div className="container-acupedia">
          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center p-6">
              <div className="w-16 h-16 rounded-full bg-white/10 flex items-center justify-center mx-auto mb-4">
                <Shield className="w-8 h-8 text-acupedia-gold" />
              </div>
              <h3 className="font-heading text-xl mb-2">Siguranță pe primul loc</h3>
              <p className="text-stone-300 text-sm">
                Verificăm contraindicațiile și interacțiunile medicamentoase pentru fiecare rețetă.
              </p>
            </div>
            <div className="text-center p-6">
              <div className="w-16 h-16 rounded-full bg-white/10 flex items-center justify-center mx-auto mb-4">
                <Leaf className="w-8 h-8 text-acupedia-gold" />
              </div>
              <h3 className="font-heading text-xl mb-2">Medicina tradițională</h3>
              <p className="text-stone-300 text-sm">
                Rețete bazate pe principiile medicinei tradiționale chineze și fitoterapiei europene.
              </p>
            </div>
            <div className="text-center p-6">
              <div className="w-16 h-16 rounded-full bg-white/10 flex items-center justify-center mx-auto mb-4">
                <Zap className="w-8 h-8 text-acupedia-gold" />
              </div>
              <h3 className="font-heading text-xl mb-2">Dozaj precis</h3>
              <p className="text-stone-300 text-sm">
                Cantități exacte în grame și măsuri casnice pentru preparare ușoară acasă.
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
